import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  FormControl,
  FormLabel,
  SimpleGrid,
  Stack,
} from '@chakra-ui/react';
import { useFetchUsersPageQuery } from '../../api';
import { useAuth } from '../../core';
import { AppInput } from '../components';
import { DatePicker } from '../form_fields';
import { FormButtonGroup, UserAutoComplete } from '../components';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PutAfeRequest } from '../../api/bindings/PutAfeRequest';

export interface AfeUpdateFormDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  afe: PutAfeRequest;
  onSubmit: (form: PutAfeRequest & { includeRevision: boolean }) => void;
  onClose: () => void;
}

interface Form extends PutAfeRequest {
  includeRevision: boolean;
}

export const AfeUpdateFormDrawerContent = (
  props: AfeUpdateFormDrawerContentProps
) => {
  const { afe, onSubmit, onClose, ...rest } = props;

  // Fetches
  const auth = useAuth();
  const { data: users } = useFetchUsersPageQuery(
    { clientId: auth?.user.clientId ?? '0', all: true },
    { skip: !auth?.user.clientId }
  );

  // State
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      id: afe.id,
      name: afe.name,
      description: afe.description,
      year: afe.year,
      assigneeUserId: afe.assigneeUserId,
      budgetStart: afe.budgetStart,
      budgetEnd: afe.budgetEnd,
      estimatedStartDate: afe.estimatedStartDate,
      estimatedEndDate: afe.estimatedEndDate,
      actualStartDate: afe.actualStartDate,
      actualEndDate: afe.actualEndDate,
      includeRevision: false,
    },
  });
  const { control, register, watch, handleSubmit } = form;
  const formName = watch('name');

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>Update AFE: {formName}</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel htmlFor="name">Name:</FormLabel>
                <AppInput
                  id="name"
                  {...register('name', {
                    minLength: {
                      value: 3,
                      message: 'Name must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Name must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="description">Description:</FormLabel>
                <AppInput
                  id="description"
                  {...register('description', {
                    minLength: {
                      value: 3,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message:
                        'Description must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="asignee">Asignee:</FormLabel>
                <Controller
                  control={control}
                  name="assigneeUserId"
                  rules={{
                    validate: (value) =>
                      value === null ||
                      BigInt(value) > 0 ||
                      'Please select a valid assignee',
                  }}
                  render={(p) => (
                    <UserAutoComplete
                      id="asignee"
                      items={users?.items}
                      {...p.field}
                      onChange={(userId) => p.field.onChange(userId)}
                    />
                  )}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="year">Year:</FormLabel>
                <DatePicker id="year" control={control} name="year" />
              </FormControl>
              <SimpleGrid columns={[1, 2]} gap={[1, 4]}>
                <FormControl>
                  <FormLabel htmlFor="estStart">Estimated Start Date</FormLabel>
                  <DatePicker
                    id="estStart"
                    control={control}
                    name="estimatedStartDate"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="estEnd">Estimated End Date</FormLabel>
                  <DatePicker
                    id="estEnd"
                    control={control}
                    name="estimatedEndDate"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="actualStart">Actual Start</FormLabel>
                  <DatePicker
                    id="actualStart"
                    name="actualStartDate"
                    control={control}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="actualEnd">Actual End</FormLabel>
                  <DatePicker
                    id="actualEnd"
                    name="actualEndDate"
                    control={control}
                  />
                </FormControl>
              </SimpleGrid>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onCancel={onClose}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
